import { ROLES, hasCapability } from '../../../utils/roles.js';
import { M } from '../msg.js';
import { registerPageTour } from '../matchRoute.js';

registerPageTour({
  id: 'my-dashboard',
  match: (p) => p === '/my/dashboard',
  steps: [
    {
      id: 'intro',
      selector: '[data-tour="page-title"]',
      title: M('Client dashboard', 'Dashboard клиента'),
      body: M(
        'Your home in the portal: project progress, agency news, time balance, and quick links to files and tasks.',
        'Главная портала: прогресс проектов, новости агентства, баланс часов и ссылки на файлы и задачи.',
      ),
      roles: [ROLES.CLIENT],
    },
    {
      id: 'stats',
      selector: '[data-tour="tour-dashboard-stats"]',
      title: M('Your overview', 'Ваш обзор'),
      body: M(
        'Counts for projects, pending tasks, forms, and asset requests. Red highlights need your action.',
        'Счётчики проектов, задач, форм и asset requests. Красное — требует действия.',
      ),
      roles: [ROLES.CLIENT],
    },
    {
      id: 'news',
      selector: '[data-tour="dashboard-news"]',
      title: M('Agency announcements', 'Объявления агентства'),
      body: M(
        'Important updates from your agency — launches, maintenance windows, or policy changes.',
        'Важные сообщения от агентства — запуски, maintenance, политики.',
      ),
      roles: [ROLES.CLIENT],
    },
    {
      id: 'time',
      selector: '[data-tour="dashboard-time"]',
      title: M('Retainer balance', 'Баланс ретейнера'),
      body: M(
        'When your plan includes time tracking, see hours used vs limit. Contact your agency if you are near the cap.',
        'При учёте времени — использованные часы и лимит. У лимита свяжитесь с агентством.',
      ),
      roles: [ROLES.CLIENT],
      capabilities: ['view_time_balance'],
    },
  ],
});

registerPageTour({
  id: 'my-projects',
  match: (p) => p === '/my/projects',
  steps: [
    {
      id: 'intro',
      selector: '[data-tour="page-title"]',
      title: M('Your projects', 'Ваши проекты'),
      body: M(
        'Every engagement your agency shares with you. Open a project for files, FAQ, and integration widgets.',
        'Все проекты, которые агентство открыло вам. Open — файлы, FAQ и виджеты.',
      ),
      roles: [ROLES.CLIENT],
      capabilities: ['view_portal_projects'],
    },
    {
      id: 'list',
      selector: '[data-tour="projects-list"]',
      title: M('Project list', 'Список проектов'),
      body: M(
        'Status and type show where each deliverable stands. Click Open for the full project workspace.',
        'Статус и тип показывают этап. Open — полная карточка проекта.',
      ),
      roles: [ROLES.CLIENT],
      capabilities: ['view_portal_projects'],
    },
  ],
});

registerPageTour({
  id: 'my-project-detail',
  match: (p) => /^\/my\/projects\/\d+$/.test(p),
  steps: [
    {
      id: 'intro',
      selector: '[data-tour="page-title"]',
      title: M('Project details', 'Детали проекта'),
      body: M(
        'Read-only view of delivery status, shared media, and tools your agency configured for this project.',
        'Статус, медиа и tools, которые агентство настроило для этого проекта.',
      ),
      roles: [ROLES.CLIENT],
      capabilities: ['view_portal_projects'],
    },
    {
      id: 'tabs',
      selector: '[data-tour="project-tabs"]',
      title: M('Sections', 'Разделы'),
      body: M(
        'Switch between overview, shared files, FAQ answers, and linked tools (Figma, analytics, etc.).',
        'Overview, файлы, FAQ и связанные tools (Figma, аналитика и т.д.).',
      ),
      roles: [ROLES.CLIENT],
      capabilities: ['view_portal_projects'],
    },
  ],
});

registerPageTour({
  id: 'my-files',
  match: (p) => p === '/my/files',
  steps: [
    {
      id: 'intro',
      selector: '[data-tour="page-title"]',
      title: M('Files', 'Файлы'),
      body: M(
        'Documents and downloads your agency shared — contracts, deliverables, and uploads from SharePoint sync.',
        'Документы и загрузки от агентства — договоры, deliverables, SharePoint sync.',
      ),
      roles: [ROLES.CLIENT],
      capabilities: ['view_portal_projects'],
    },
    {
      id: 'list',
      selector: '[data-tour="files-list"]',
      title: M('File list', 'Список файлов'),
      body: M(
        'Click to download or open external links. New files appear after agency upload or sync.',
        'Скачать или открыть ссылку. Новые файлы — после загрузки или sync агентством.',
      ),
      roles: [ROLES.CLIENT],
      capabilities: ['view_portal_projects'],
    },
  ],
});

registerPageTour({
  id: 'my-tools',
  match: (p) => p === '/my/tools',
  steps: [
    {
      id: 'intro',
      selector: '[data-tour="page-title"]',
      title: M('Tools & links', 'Tools и ссылки'),
      body: M(
        'Quick access links your agency pinned — design files, dashboards, hosting panels, and more.',
        'Закреплённые ссылки агентства — дизайн, дашборды, хостинг и др.',
      ),
      roles: [ROLES.CLIENT],
    },
    {
      id: 'list',
      selector: '[data-tour="tools-list"]',
      title: M('Link cards', 'Карточки ссылок'),
      body: M(
        'Each card opens in a new tab. Ask your agency to add links on the client record if something is missing.',
        'Каждая карточка открывается в новой вкладке. Новые ссылки добавляет агентство.',
      ),
      roles: [ROLES.CLIENT],
    },
  ],
});

registerPageTour({
  id: 'my-tasks',
  match: (p) => p === '/my/tasks',
  steps: [
    {
      id: 'intro',
      selector: '[data-tour="page-title"]',
      title: M('Your tasks', 'Ваши задачи'),
      body: M(
        'Action items assigned to you by the agency. Mark complete when done — staff see updates immediately.',
        'Задачи от агентства. Отмечайте выполненными — staff видит сразу.',
      ),
      roles: [ROLES.CLIENT],
      capabilities: ['view_client_tasks'],
    },
    {
      id: 'list',
      selector: '[data-tour="tasks-list"]',
      title: M('Task board', 'Доска задач'),
      body: M(
        'Open a card to read instructions. Drag it to another column when you start or finish the work.',
        'Откройте карточку, чтобы прочитать инструкции. Перетащите её в другую колонку, когда начнёте или закончите.',
      ),
      roles: [ROLES.CLIENT],
      capabilities: ['view_client_tasks'],
    },
  ],
});

registerPageTour({
  id: 'my-assets',
  match: (p) => p === '/my/assets',
  steps: [
    {
      id: 'intro',
      selector: '[data-tour="page-title"]',
      title: M('Media & assets', 'Медиа и ассеты'),
      body: M(
        'Download approved creative files and submit asset requests when you need new materials.',
        'Скачивайте одобренные файлы и отправляйте запросы на новые материалы.',
      ),
      roles: [ROLES.CLIENT],
      capabilities: ['view_client_assets'],
    },
    {
      id: 'tabs',
      selector: '[data-tour="assets-tabs"]',
      title: M('Library & requests', 'Библиотека и запросы'),
      body: M(
        'Browse the library for ready assets. Use Requests to ask the agency for logos, photos, or copy.',
        'Библиотека готовых ассетов. Requests — запрос логотипов, фото или текстов.',
      ),
      roles: [ROLES.CLIENT],
      capabilities: ['view_client_assets'],
    },
  ],
});

registerPageTour({
  id: 'my-forms',
  match: (p) => p === '/my/forms' || p === '/my/faq',
  steps: [
    {
      id: 'intro',
      selector: '[data-tour="page-title"]',
      title: M('Forms & questionnaires', 'Формы и анкеты'),
      body: M(
        'Intake forms and FAQ hub your agency built for you. Fill required fields so delivery can continue.',
        'Формы и FAQ от агентства. Заполните обязательные поля для продолжения работы.',
      ),
      roles: [ROLES.CLIENT],
      capabilities: ['view_client_faq'],
    },
    {
      id: 'content',
      selector: '[data-tour="faq-content"]',
      title: M('FAQ hub', 'FAQ-хаб'),
      body: M(
        'Categories may include credentials, file uploads, and questionnaires. Submit saves answers for your team.',
        'Категории: credentials, файлы, анкеты. Submit сохраняет ответы для команды.',
      ),
      roles: [ROLES.CLIENT],
      capabilities: ['view_client_faq'],
    },
  ],
});

registerPageTour({
  id: 'my-emails',
  match: (p) => p === '/my/emails',
  steps: [
    {
      id: 'intro',
      selector: '[data-tour="page-title"]',
      title: M('Emails', 'Почта'),
      body: M(
        'Shared mailbox threads your agency linked to your account. Read-only view for transparency.',
        'Потоки общего ящика, привязанного к вашему аккаунту. Только просмотр.',
      ),
      roles: [ROLES.CLIENT],
      when: (u) => hasCapability(u, 'view_client_emails'),
    },
    {
      id: 'list',
      selector: '[data-tour="mailbox-list"]',
      title: M('Message list', 'Список писем'),
      body: M(
        'Recent correspondence related to your projects. Contact your agency if you need a reply sent.',
        'Переписка по проектам. Для ответа обратитесь к агентству.',
      ),
      roles: [ROLES.CLIENT],
      when: (u) => hasCapability(u, 'view_client_emails'),
    },
  ],
});

registerPageTour({
  id: 'my-meetings',
  match: (p) => p === '/my/meetings',
  steps: [
    {
      id: 'intro',
      selector: '[data-tour="page-title"]',
      title: M('Meetings', 'Встречи'),
      body: M(
        'Upcoming video calls and meetings from the agency calendar linked to your portal.',
        'Предстоящие звонки из календаря агентства, привязанного к порталу.',
      ),
      roles: [ROLES.CLIENT],
      when: (u) => hasCapability(u, 'view_client_meetings'),
    },
    {
      id: 'list',
      selector: '[data-tour="mailbox-list"]',
      title: M('Calendar items', 'События календаря'),
      body: M(
        'Check time and subject before each call. Join links open in your calendar app when available.',
        'Время и тема перед звонком. Ссылки join — в календарном приложении.',
      ),
      roles: [ROLES.CLIENT],
      when: (u) => hasCapability(u, 'view_client_meetings'),
    },
  ],
});
